// Keyboard map (§10). Disabled while typing in an input, select or textarea.
import { useEffect } from 'react';
import { useLibrary } from '../state/library';
import { useEditor } from '../state/editor';
import { useUi } from '../state/ui';
import type { PhotoMeta } from '../model/types';
import { useClipboard } from '../state/clipboard';
import { removeBackground } from '../features/background/actions';
import { useWorkspace } from '../state/workspace';

const typing = (t: EventTarget | null) =>
  t instanceof HTMLInputElement ||
  t instanceof HTMLTextAreaElement ||
  t instanceof HTMLSelectElement ||
  (t instanceof HTMLElement && t.isContentEditable);

/** Rating/reject targets: the active photo in cull view, otherwise the whole selection. */
function targets(): string[] {
  const lib = useLibrary.getState();
  if (lib.cull || lib.selected.size === 0) return lib.activeId ? [lib.activeId] : [];
  return [...lib.selected];
}

export function useKeyboard() {
  useEffect(() => {
    let beforeDown = 0;
    let beforeWas = false;
    const onDown = (e: KeyboardEvent) => {
      if (typing(e.target) || useWorkspace.getState().ws === 'design') return; // Design has its own map
      const ui = useUi.getState();
      const lib = useLibrary.getState();
      const id = lib.activeId;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 'e' || e.key === 'E')) {
        if (id) ui.set({ exportOpen: true });
        e.preventDefault();
        return;
      }
      if (mod && (e.key === 'z' || e.key === 'Z')) {
        if (id) (e.shiftKey ? useEditor.getState().redo : useEditor.getState().undo)(id);
        e.preventDefault();
        return;
      }
      if (mod && (e.key === 'a' || e.key === 'A')) {
        lib.selectAll();
        e.preventDefault();
        return;
      }
      if (mod) return;
      if (/^[0-5]$/.test(e.key)) {
        lib.rate(targets(), Number(e.key) as PhotoMeta['rating']);
        if (lib.cull && lib.autoAdvance) lib.step(1);
        e.preventDefault();
        return;
      }
      switch (e.key) {
        case 'c':
        case 'C':
          useClipboard.getState().copy(e.shiftKey);
          break;
        case 'v':
        case 'V':
          useClipboard.getState().paste(e.shiftKey);
          break;
        case 'ArrowLeft':
          lib.step(-1);
          break;
        case 'ArrowRight':
          lib.step(1);
          break;
        case 'x':
        case 'X':
          lib.toggleReject(targets());
          if (lib.cull && lib.autoAdvance) lib.step(1);
          break;
        case 'e':
        case 'E':
        case 'f':
        case 'F':
          if (lib.photos.length) lib.set({ cull: !lib.cull });
          break;
        case '\\':
          if (!e.repeat) {
            beforeDown = performance.now();
            beforeWas = ui.before;
            ui.set({ before: true });
          }
          break;
        case 'y':
        case 'Y':
          ui.set({ split: !ui.split });
          break;
        case '?':
          ui.set({ cheatOpen: !ui.cheatOpen });
          break;
        case 'b':
        case 'B':
          void removeBackground(targets());
          break;
        case 'w':
        case 'W':
          ui.set({ bgView: (ui.bgView % 5) + 1 });
          break;
        case 'o':
        case 'O':
          ui.set({ maskOverlay: !ui.maskOverlay });
          break;
        case 'j':
        case 'J':
          ui.set({ clipping: !ui.clipping });
          break;
        case 'z':
        case 'Z':
          ui.set({ zoom: ui.zoom === 'fit' ? 1 : 'fit' });
          break;
        case 'r':
        case 'R':
          ui.set({ tool: ui.tool === 'crop' ? 'none' : 'crop' });
          break;
        case 'Escape':
          if (lib.cull) lib.set({ cull: false });
          else if (ui.tool !== 'none') ui.set({ tool: 'none' });
          break;
        case 'Enter':
          if (ui.tool !== 'none') ui.set({ tool: 'none' });
          break;
        default:
          return;
      }
      e.preventDefault();
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key !== '\\') return;
      // Tap toggles; hold shows "before" only while held.
      const held = performance.now() - beforeDown > 250;
      useUi.getState().set({ before: held ? beforeWas : !beforeWas });
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);
}
